import { useEffect, useRef, useState } from 'react'
import {
  VisualCrop,
  VisualShape,
  VisualZoom,
} from 'app/frontend/types/custom/VisualPlan'
import {
  DEFAULT_TEXT_BACKGROUND,
  DEFAULT_TEXT_COLOR,
  TEXT_FONT_FAMILY,
  TEXT_LINE_HEIGHT,
  TEXT_PADDING_X,
  TEXT_PADDING_Y,
  stageViewAttrs,
} from '../constants'
import { measureTextBoxHeightPx } from '../shapes/textLayout'

type Props = {
  shape: Extract<VisualShape, { type: 'text' }>
  canvasWidth: number
  canvasHeight: number
  zoom: VisualZoom | undefined
  crop?: VisualCrop
  onCommit: (text: string) => void
  onCancel: () => void
}

const TextEditorOverlay = ({
  shape,
  canvasWidth,
  canvasHeight,
  zoom,
  crop,
  onCommit,
  onCancel,
}: Props) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [draft, setDraft] = useState(shape.text || 'Text')
  const committedRef = useRef(false)

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.focus()
    textarea.select()
  }, [])

  const finish = (value: string) => {
    if (committedRef.current) return
    committedRef.current = true
    onCommit(value)
  }

  const {
    x: stageX,
    y: stageY,
    scaleX: scale,
  } = stageViewAttrs(zoom, crop, canvasWidth, canvasHeight)

  const boxWidthPx = shape.width * canvasWidth
  const fontSizePx = shape.font_size * canvasWidth
  const boxHeightPx = measureTextBoxHeightPx(draft, boxWidthPx, fontSizePx)
  const centerX = shape.x * canvasWidth
  const centerY = shape.y * canvasHeight

  const left = (centerX - boxWidthPx / 2) * scale + stageX
  const top = (centerY - boxHeightPx / 2) * scale + stageY

  const textColor = shape.fill_rgb ?? DEFAULT_TEXT_COLOR
  const backgroundColor = shape.stroke_rgb ?? DEFAULT_TEXT_BACKGROUND

  return (
    <textarea
      ref={textareaRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => finish(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          committedRef.current = true
          onCancel()
        }
      }}
      rows={1}
      className='absolute z-20 resize-none overflow-hidden rounded border-0 text-center outline-none ring-0'
      style={{
        left,
        top,
        width: boxWidthPx * scale,
        height: boxHeightPx * scale,
        boxSizing: 'border-box',
        margin: 0,
        padding: `${TEXT_PADDING_Y * scale}px ${TEXT_PADDING_X * scale}px`,
        transform: `rotate(${shape.rotation ?? 0}deg)`,
        transformOrigin: 'center center',
        fontSize: fontSizePx * scale,
        fontFamily: TEXT_FONT_FAMILY,
        lineHeight: TEXT_LINE_HEIGHT,
        color: textColor,
        backgroundColor,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    />
  )
}

export default TextEditorOverlay
